import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface TemplateSummary {
  id: string;
  name: string;
  documentType: string;
  currentVersionId: string | null;
}

interface TemplateVersion {
  id: string;
  versionNumber: number;
  body: string;
  variablesSchema: string[];
  createdAt: string | null;
}

interface TemplateDetail extends TemplateSummary {
  requiredForStageIds: string[];
  archivedAt: string | null;
  versions: TemplateVersion[];
}

/**
 * Story 7.6 — template library management UI. Lists tenant templates,
 * shows the current version + history, lets the admin save a new
 * version or create a brand new template.
 */
export function DocumentTemplatesPage() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [detail, setDetail] = useState<TemplateDetail | null>(null);
  const [newName, setNewName] = useState('');
  const [newBody, setNewBody] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  async function refreshList() {
    const { data } = await apiClient.get<TemplateSummary[]>('/document-templates');
    setTemplates(data);
  }

  useEffect(() => {
    refreshList().catch(() => setTemplates([]));
  }, []);

  async function openTemplate(id: string) {
    const { data } = await apiClient.get<TemplateDetail>(`/document-templates/${id}`);
    setDetail(data);
  }

  async function createTemplate() {
    if (!newName.trim() || !newBody.trim()) return;
    try {
      await apiClient.post('/document-templates', {
        name: newName,
        documentType: 'GENERIC',
        body: newBody,
        variablesSchema: ['account', 'contact', 'opportunity', 'campaign', 'template', 'stage'],
      });
      setNewName('');
      setNewBody('');
      setMessage(t('docTemplates.created'));
      await refreshList();
    } catch {
      setMessage(t('docTemplates.createError'));
    }
  }

  async function saveNewVersion() {
    if (!detail) return;
    const body = window.prompt(t('docTemplates.newBodyPrompt'), detail.versions[0]?.body ?? '');
    if (!body) return;
    await apiClient.post(`/document-templates/${detail.id}/versions`, {
      body,
      variablesSchema: detail.versions[0]?.variablesSchema ?? [],
    });
    await openTemplate(detail.id);
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">{t('docTemplates.title')}</h1>
      <div className="grid gap-4 md:grid-cols-[16rem_1fr]">
        <ul className="divide-y rounded-lg border bg-card">
          {templates.map((tpl) => (
            <li key={tpl.id}>
              <button
                type="button"
                onClick={() => openTemplate(tpl.id)}
                className="block w-full px-4 py-3 text-left hover:bg-accent"
              >
                <div className="font-medium">{tpl.name}</div>
                <div className="text-xs text-muted-foreground">{tpl.documentType}</div>
              </button>
            </li>
          ))}
          {templates.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-muted-foreground">—</li>
          )}
        </ul>

        <div className="space-y-4">
          <div className="rounded-lg border bg-card p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {t('docTemplates.newTemplate')}
            </h2>
            <div className="mt-3 space-y-2">
              <Input
                placeholder={t('docTemplates.namePlaceholder')}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <textarea
                className="min-h-32 w-full rounded-md border bg-background p-2 font-mono text-sm"
                placeholder={t('docTemplates.bodyPlaceholder')}
                value={newBody}
                onChange={(e) => setNewBody(e.target.value)}
              />
              <Button size="sm" onClick={createTemplate}>
                {t('docTemplates.create')}
              </Button>
              {message && <p className="text-xs text-muted-foreground">{message}</p>}
            </div>
          </div>

          {detail && (
            <div className="rounded-lg border bg-card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">{detail.name}</h2>
                  <div className="text-xs text-muted-foreground">
                    {t('docTemplates.versionCount', { n: detail.versions.length })}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      window.open(`/api/v1/document-templates/${detail.id}/pdf`, '_blank');
                    }}
                  >
                    {t('docTemplates.exportPdf')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={saveNewVersion}>
                    {t('docTemplates.saveNewVersion')}
                  </Button>
                </div>
              </div>
              <pre className="mt-4 max-h-64 overflow-auto rounded-md bg-muted p-3 text-xs">
                {detail.versions[0]?.body ?? ''}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
